import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = "admin" | "creator" | "shopper";

/**
 * Single source of truth for the current user's roles. Reads from
 * public.user_roles (RLS: users can read their own rows). Fail-closed:
 * on error or no session, returns an empty role set.
 */
export function useRoles() {
  const { user, loading: authLoading } = useAuth();
  const q = useQuery({
    queryKey: ["my-roles", user?.id],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async (): Promise<AppRole[]> => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user!.id);
      if (error) {
        console.error("[useRoles] role lookup failed", error);
        return [];
      }
      return (data ?? []).map((r) => r.role as AppRole);
    },
  });

  const roles = q.data ?? [];
  const has = (r: AppRole) => roles.includes(r);
  return {
    roles,
    isAdmin: has("admin"),
    isCreator: has("creator"),
    isShopper: has("shopper"),
    hasRole: has,
    loading: authLoading || (!!user && q.isLoading),
  };
}
